import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import SplitPane from 'react-split-pane'
import { useSelector, useDispatch } from 'react-redux'
import produce from 'immer'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'
import Button from 'src/components/base/Button'
import useTerminalConfiguration from 'src/hooks/api/useTerminalConfiguration'
import Skeleton from 'src/components/base/Skeleton'
import { confirmPopup } from 'src/components/popup/confirmPopup'
import useWaitingState from 'src/hooks/useWaitingState'
import { reportPageView } from 'src/utils/analyticsUtils'
import { handleFetchError } from 'src/js/sentry'
import { userPermissionRoles } from 'src/utils/userPermissions'

import TerminalOperations from './TerminalOperations'
import { Config, RootState } from '../../types'
import TerminalPreview from '../../components/configuration/terminalPreview/TerminalPreview'
import useSidebar from '../../components/sidebar/useSidebar'
import Sidebar from '../../components/sidebar/Sidebar'
import SidebarTabContent from '../../components/sidebar/SidebarTabContent'
import SidebarContent from '../../components/sidebar/SidebarContent'
import TerminalProducts from './TerminalProducts'
import QuayConfiguration from './QuayConfiguration'
import { INITIAL_QUAY } from './constants'
import {
  getQuayErrors,
  getTerminalOperationsErrors,
  getMobruErrors,
  getJettyErrors,
  getProductErrors,
  getGlobalProductUnitInconsistencies
} from './utils'
import { ConfigProvider } from './ConfigContext'
import { setTerminalConfig } from '../../components/app/actions'

import './Configuration.scss'

export type TPropsConfiguration = {
  customFetch: (url: string, options: RequestInit, customResolver?: (r: Response) => any) => Promise<any>
}

const ConfigurationSkeleton = () => {
  return (
    <div className="configuration-page main">
      <div className="box-element" style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 10, padding: 10 }}>
        {/* Skeleton as Terminal Preview */}
        <Skeleton style={{ height: 100, width: '100%' }} />
        {/* Skeleton as Sidebar and SidebarTabContent */}
        <div style={{ display: 'flex', flex: 1, gap: 10 }}>
          <Skeleton style={{ width: 280, height: '100%' }} />
          <Skeleton style={{ flex: 1, height: '100%' }} />
        </div>
      </div>
    </div>
  )
}

const Configuration = ({ customFetch }: TPropsConfiguration) => {
  const [config, setConfig] = useState<Config | null>(null)
  const [isShowingSuccessUpdateConfig, setIsShowingSuccessUpdateConfig] = useWaitingState()
  const dispatch = useDispatch()

  const auth = useSelector((state: RootState) => state.login.auth)
  const history = useHistory()

  // Permission check: redirect non-planners to root
  const { planner: isPlanner } = userPermissionRoles(auth.roles)
  useEffect(() => {
    if (!isPlanner) {
      history.replace('/')
    }
  }, [isPlanner, history])

  const {
    data,
    isLoading,
    mutation: { mutate: saveConfig, isLoading: isSavingConfig }
  } = useTerminalConfiguration(
    {
      terminalId: auth.terminalId
    },
    {
      onSuccess: (data: Config) => {
        // update the store
        dispatch(setTerminalConfig(data))
      },
      onError: (err: Error) => {
        handleFetchError(err, 'Failed to load configuration')
      }
    }
  )

  const quayNames = useMemo(() => {
    return config?.quays.map(quay => quay.name) ?? []
  }, [config])

  const getQuayConfigByIndex = useCallback(
    (index: number) => {
      return config?.quays[index]
    },
    [config]
  )

  useEffect(() => {
    if (data) {
      setConfig(data)
    }
  }, [data])

  useEffect(() => {
    reportPageView('/configuration/terminal')
  }, [])

  const isConfigChanges = useMemo(() => {
    // check full object compare
    return JSON.stringify(config) !== JSON.stringify(data)
  }, [config, data])

  // Navigation guard: block route changes when there are unsaved changes
  const unblockRef = useRef<null | (() => void)>(null)
  const nextLocationRef = useRef<null | { pathname: string; search?: string; state?: any }>(null)

  useEffect(() => {
    // Always clear any previous block before (re)establishing
    if (unblockRef.current) {
      unblockRef.current()
      unblockRef.current = null
    }

    if (isConfigChanges) {
      // Start blocking transitions
      unblockRef.current = history.block((tx: any) => {
        // Store the next location so we can navigate to it on confirm
        nextLocationRef.current = tx

        // Show confirmation popup
        confirmPopup({
          title: 'Unsaved changes',
          message: 'Leave this page? Your unsaved changes will be lost.',
          cancelText: 'Stay',
          confirmText: 'Leave without saving',
          confirmClassName: 'btn danger-btn',
          onConfirm: () => {
            const next = nextLocationRef.current
            if (next) {
              // Unblock and continue navigation
              if (unblockRef.current) {
                unblockRef.current()
                unblockRef.current = null
              }
              history.push(`${next.pathname}${next.search || ''}`, (next as any).state)
            }
          }
        })

        // Cancel this transition for now; we'll continue it on confirm
        return false
      })
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (unblockRef.current) {
        unblockRef.current()
        unblockRef.current = null
      }
    }
  }, [isConfigChanges, history])

  // Warn on browser refresh/tab close when there are unsaved changes
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isConfigChanges) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [isConfigChanges])

  const addQuay = () => {
    setConfig(
      produce((draft: Config) => {
        draft.quays.push(INITIAL_QUAY)
      })
    )
  }

  const { config: sidebarConfig, setActive } = useSidebar({
    title: 'Configuration',
    menus: [
      { name: 'Terminal operations', group: 'Terminal configuration', icon: 'fa fa-anchor' },
      { name: 'Terminal products overview', group: 'Terminal configuration', icon: 'fa fa-cube' },
      ...quayNames.map((name, index) => ({
        name: `quay-${index}`,
        label: name,
        group: 'Quay configuration',
        icon: 'fa fa-hashtag'
      })),
      {
        name: 'Add quay',
        group: 'Quay configuration',
        icon: 'fa fa-plus',
        type: 'button',
        action: () => {
          addQuay()
          // select the newly added quay; URL will sync via effect
          setActive(`quay-${quayNames.length}`)
        }
      }
    ]
  })

  const errorCheckingHasError = useCallback(() => {
    if (!config) return true

    let foundError = false

    // Terminal operations validation
    const terminalErrors = getTerminalOperationsErrors(config)
    if (terminalErrors.length > 0) {
      terminalErrors.forEach(error => toast.error(error))
      foundError = true
    }

    // Quay-level + holder + product validation
    config.quays.forEach((quay, quayIndex) => {
      const quayErrors = getQuayErrors(quay)
      if (quayErrors.length > 0) {
        quayErrors.forEach(error => toast.error(`Quay ${quayIndex + 1}: ${error}`))
        foundError = true
      }

      // Mobrus
      quay.mobrus?.forEach(mobru => {
        const mErrors = getMobruErrors(mobru as any)
        if (mErrors.length > 0) {
          mErrors.forEach(error => toast.error(`Quay ${quayIndex + 1}, Cargo belts ${mobru.name || ''}: ${error}`))
          foundError = true
        }
        // Products under mobru
        mobru.products?.forEach((product, productIndex) => {
          const pErrors = getProductErrors(product)
          if (pErrors.length > 0) {
            pErrors.forEach(error =>
              toast.error(
                `Quay ${quayIndex + 1}, Cargo belts ${mobru.name || ''}, Product ${
                  product.product || productIndex + 1
                }: ${error}`
              )
            )
            foundError = true
          }
        })
      })

      // Jetties
      quay.jetties?.forEach(jetty => {
        const jErrors = getJettyErrors(jetty as any)
        if (jErrors.length > 0) {
          jErrors.forEach(error => toast.error(`Quay ${quayIndex + 1}, Jetty ${jetty.name || ''}: ${error}`))
          foundError = true
        }
        // Products under jetty
        jetty.products?.forEach((product, productIndex) => {
          const pErrors = getProductErrors(product)
          if (pErrors.length > 0) {
            pErrors.forEach(error =>
              toast.error(
                `Quay ${quayIndex + 1}, Jetty ${jetty.name || ''}, Product ${
                  product.product || productIndex + 1
                }: ${error}`
              )
            )
            foundError = true
          }
        })
      })
    })

    // Global product unit consistency across holders
    const diffs = getGlobalProductUnitInconsistencies(config)
    if (diffs.length > 0) {
      toast.error('Invalid products: all products with the same name should have the same unit')
      diffs.forEach(d =>
        toast.error(`Product ${d.product} (Group ${d.productGroup}) has multiple units: ${d.units.join(', ')}`)
      )
      foundError = true
    }

    return foundError
  }, [config])

  const onSaveConfig = () => {
    if (config) {
      if (errorCheckingHasError()) {
        return
      }

      saveConfig(
        { terminalId: auth.terminalId, config },
        {
          onSuccess: () => {
            // call popup with success message
            toast.success('Configuration saved')
            setIsShowingSuccessUpdateConfig(true)
          },
          onError: (err: Error) => {
            handleFetchError(err, 'Failed to save configuration')
          }
        }
      )
    }
  }

  const onResetConfig = () => {
    confirmPopup({
      title: 'Reset configuration',
      message: 'Are you sure you want to reset the configuration? All unsaved changes will be lost.',
      confirmText: 'Reset',
      confirmClassName: 'btn danger-btn',
      onConfirm: () => {
        if (data) {
          setConfig(data)
        }
      }
    })
  }

  // spinner
  if (isLoading) {
    return <ConfigurationSkeleton />
  }

  return (
    <div className="configuration-page main">
      {config && (
        <ConfigProvider config={config} setConfig={setConfig}>
          <SplitPane split="horizontal" defaultSize={150} minSize={150} pane2Style={{ overflow: 'auto' }}>
            <div className="preview-container">
              <TerminalPreview config={config} />
            </div>
            <div className="frame-root" style={{ display: 'flex' }}>
              <Sidebar config={sidebarConfig}>
                {isConfigChanges && (
                  <>
                    <div className="neutral-tag">
                      {'You\'ve made changes that haven\'t been saved yet. Please click "Save Changes" to continue.'}
                    </div>
                    <Button
                      disabled={isSavingConfig || isShowingSuccessUpdateConfig}
                      variant="danger-primary"
                      onClick={onResetConfig}
                    >
                      Reset changes
                    </Button>
                  </>
                )}
                <Button
                  disabled={!isConfigChanges || isSavingConfig || isShowingSuccessUpdateConfig}
                  onClick={onSaveConfig}
                  loading={!!isSavingConfig}
                  variant={isShowingSuccessUpdateConfig ? 'success-primary' : 'brand-primary'}
                >
                  {isShowingSuccessUpdateConfig ? 'Saved' : 'Save changes'}
                </Button>
              </Sidebar>
              <SidebarTabContent config={sidebarConfig}>
                <SidebarContent name="Terminal operations">
                  <TerminalOperations config={config} setConfig={setConfig} />
                </SidebarContent>
                <SidebarContent name="Terminal products overview">
                  <TerminalProducts config={config} setConfig={setConfig} />
                </SidebarContent>
                {quayNames.map((name, index) => {
                  const quayConfig = getQuayConfigByIndex(index)
                  return (
                    <SidebarContent key={name} name={`quay-${index}`}>
                      <QuayConfiguration config={quayConfig} setConfig={setConfig} customFetch={customFetch} />
                    </SidebarContent>
                  )
                })}
              </SidebarTabContent>
            </div>
          </SplitPane>
        </ConfigProvider>
      )}
    </div>
  )
}

export default Configuration
